<template>
  <div class="credit-pack-markets-panel" data-testid="credit-pack-markets-panel">
    <!-- Market list -->
    <div
      v-if="editingMarket == null"
      class="pack-prices-list"
      data-testid="pack-prices-list"
    >
      <div
        v-for="market in currentMarkets"
        :key="`${market.currency}-${market.provider_code}`"
        class="pack-prices-row"
        :data-testid="`pack-prices-market-row-${market.currency}-${market.provider_code}`"
      >
        <span
          class="market-currency"
          :data-testid="`pack-prices-currency-${market.currency}-${market.provider_code}`"
        >{{ market.currency }}</span>
        <span
          class="market-provider"
          :data-testid="`pack-prices-provider-${market.currency}-${market.provider_code}`"
        >{{ market.provider_code }}</span>
        <span
          class="market-price"
          :data-testid="`pack-prices-price-${market.currency}-${market.provider_code}`"
        >{{ formatPrice(market) }}</span>
        <!-- Same gap as PlanMarketsPanel: editable below, invisible here. -->
        <span
          class="market-countries"
          :data-testid="`pack-prices-countries-${market.currency}-${market.provider_code}`"
        >{{ (market.countries ?? []).join(', ') || '—' }}</span>
        <Button
          :label="t('Editar')"
          severity="secondary"
          size="small"
          :data-testid="`pack-prices-edit-market-${market.currency}-${market.provider_code}`"
          @click="startEditMarket(market)"
        />
      </div>

      <div class="pack-prices-add" data-testid="pack-prices-add-section">
        <Button
          :label="t('+ Adicionar mercado')"
          severity="secondary"
          data-testid="pack-prices-add-market-btn"
          @click="startAddMarket"
        />
      </div>
    </div>

    <!-- Inline add/edit form -->
    <div
      v-else
      class="pack-prices-edit-form"
      data-testid="pack-prices-edit-form"
    >
      <div class="form-fields">
        <!-- Currency: fixed in edit mode, selectable in add mode -->
        <div v-if="isAddMode" class="field-row">
          <select-list
            v-model="editingMarket.currency"
            elem-id="pack-prices-edit-currency"
            :label="t('Moeda')"
            :itens-select="currencyOptions"
            data-testid="pack-prices-edit-currency"
          />
        </div>
        <div v-else class="field-row">
          <label class="field-label">{{ t('Moeda') }}</label>
          <span
            class="field-value-static"
            data-testid="pack-prices-edit-currency-static"
          >{{ editingMarket.currency }}</span>
        </div>

        <div class="field-row">
          <select-list
            v-model="editingMarket.provider_code"
            elem-id="pack-prices-edit-gateway"
            :label="t('Gateway')"
            :itens-select="gatewayOptionsForCurrency"
            data-testid="pack-prices-edit-gateway"
          />
        </div>

        <div class="field-row">
          <input-currency
            v-model="editPriceMajor"
            elem-id="pack-prices-edit-price"
            :label="t('Preço')"
            :currency="editingMarket.currency"
            :currency-prefix="editingLocale"
            data-testid="pack-prices-edit-price"
          />
        </div>

        <div class="field-row field-full">
          <select-list-multiple
            v-model="editingMarket.countries"
            elem-id="pack-prices-edit-countries"
            :label="t('Países')"
            :itens-select="countryOptions"
            data-testid="pack-prices-edit-countries"
          />
          <small
            v-if="countriesError"
            class="countries-error"
            data-testid="pack-prices-edit-countries-error"
          >{{ countriesError }}</small>
        </div>
      </div>

      <div class="edit-form-actions">
        <Button
          :label="t('Salvar mercado')"
          :disabled="isSaving"
          data-testid="pack-markets-panel-save-market-btn"
          @click="handleSaveMarket"
        />
        <Button
          :label="t('Cancelar')"
          severity="secondary"
          size="small"
          data-testid="pack-prices-cancel-edit"
          @click="cancelEdit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../../eventBus'
import { log } from '../../../../utils/DebugLogger'
import { useCreditPacks } from '../../../../composables/useCreditPacks'
import { usePaymentGatewayRegistry } from '../../../../composables/usePaymentGatewayRegistry'
import { useMarketCountries } from '../../../../composables/useMarketCountries'
import { currencyFactor } from '../../../../utils/currencyMinorUnits'
import type { PackMarket } from '../../../../composables/useCreditPacks'

const { t, n } = useI18n()
const { packs, saveMarketForPack, fetchPacks } = useCreditPacks()
const { featureMap, loadMap } = usePaymentGatewayRegistry()
// D6: countries + currencies come from the market-countries endpoint
// (locations-service annotated by MarketPolicy) — never a hardcoded list.
const { billingReadyCountries, marketCurrencies, load: loadMarketCountries } = useMarketCountries()

const props = defineProps<{
  packId: number
}>()

const emit = defineEmits<{
  (e: 'changed'): void
}>()

// Display-formatting locale per currency — NOT a market list.
const CURRENCY_LOCALE: Record<string, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  CLP: 'es-CL',
}

/** Country options — billing-ready countries from the market-countries endpoint (D6). */
const countryOptions = billingReadyCountries

const currencyOptions = computed(() =>
  marketCurrencies.value.map((c) => ({ ID: c, Name: c })),
)

const isSaving = ref(false)
const countriesError = ref<string | null>(null)

/** Editing state: null = list view; non-null = add/edit form. */
const editingMarket = ref<{ currency: string; price_cents: number; provider_code: string; countries: string[] } | null>(null)
const isAddMode = ref(false)

/** Markets already stored for this pack in the packs ref (one row per currency×gateway). */
const currentMarkets = computed<PackMarket[]>(() => {
  const pack = packs.value.find((p) => p.id === props.packId)
  if (!pack || pack.currency == null || pack.provider_code == null) return []
  // The packs list has one row per (currency, provider_code) pair from LEFT JOIN —
  // collect all rows for this pack id.
  return packs.value
    .filter((p) => p.id === props.packId && p.currency != null && p.provider_code != null)
    .map((p) => ({
      currency: p.currency!,
      price_cents: p.price_cents ?? 0,
      provider_code: p.provider_code!,
      countries: p.countries ?? [],
    }))
})

/**
 * Gateways eligible for the currently selected currency, derived from the
 * live registry feed (usePaymentGatewayRegistry.featureMap) instead of a
 * frozen local copy. Mirrors the approach in PlanMarketsPanel.
 */
const gatewayOptionsForCurrency = computed(() => {
  const currency = editingMarket.value?.currency ?? 'BRL'
  if (!featureMap.value || !currency) return []
  const seen = new Set<string>()
  const options: { ID: string; Name: string }[] = []
  for (const providers of Object.values(featureMap.value)) {
    for (const [code, entry] of Object.entries(providers)) {
      if (seen.has(code)) continue
      const supported: string[] = (entry as { currencies?: string[] }).currencies ?? []
      if (supported.includes('*') || supported.includes(currency)) {
        seen.add(code)
        options.push({ ID: code, Name: code })
      }
    }
  }
  return options
})

onMounted(() => {
  loadMap().catch((err) => {
    log('CreditPackMarketsPanel', 'onMounted', 'Erro ao carregar mapa de gateways', err)
  })
  loadMarketCountries().catch((err) => {
    log('CreditPackMarketsPanel', 'onMounted', 'Erro ao carregar países de mercado', err)
  })
})

const editingLocale = computed(() =>
  editingMarket.value ? (CURRENCY_LOCALE[editingMarket.value.currency] ?? 'pt-BR') : 'pt-BR',
)

const editPriceMajor = computed({
  get: () => {
    if (!editingMarket.value) return 0
    const currency = editingMarket.value.currency
    const locale = CURRENCY_LOCALE[currency] ?? 'pt-BR'
    const factor = currencyFactor(locale, currency)
    return editingMarket.value.price_cents / factor
  },
  set: (val: number) => {
    if (!editingMarket.value) return
    const currency = editingMarket.value.currency
    const locale = CURRENCY_LOCALE[currency] ?? 'pt-BR'
    const factor = currencyFactor(locale, currency)
    editingMarket.value.price_cents = Math.round(val * factor)
  },
})

function formatPrice(market: PackMarket): string {
  const locale = CURRENCY_LOCALE[market.currency] ?? 'pt-BR'
  const factor = currencyFactor(locale, market.currency)
  const majorValue = market.price_cents / factor
  return n(majorValue, { style: 'currency', currency: market.currency, currencyDisplay: 'symbol' })
}

function startEditMarket(market: PackMarket): void {
  isAddMode.value = false
  editingMarket.value = {
    currency: market.currency,
    price_cents: market.price_cents,
    provider_code: market.provider_code,
    countries: [...market.countries],
  }
}

function startAddMarket(): void {
  isAddMode.value = true
  countriesError.value = null
  // Default to the first market currency (BRL first by sort) once loaded.
  editingMarket.value = {
    currency: marketCurrencies.value[0] ?? '',
    price_cents: 0,
    provider_code: '',
    countries: [],
  }
}

function cancelEdit(): void {
  editingMarket.value = null
  isAddMode.value = false
  countriesError.value = null
}

async function handleSaveMarket(): Promise<void> {
  if (!editingMarket.value) return

  if (editingMarket.value.countries.length === 0) {
    countriesError.value = t('Selecione pelo menos um país')
    eventBus.emit('globalMessage.error', t('Selecione pelo menos um país'))
    return
  }
  countriesError.value = null

  try {
    isSaving.value = true
    const market: PackMarket = {
      currency: editingMarket.value.currency,
      price_cents: editingMarket.value.price_cents,
      provider_code: editingMarket.value.provider_code,
      countries: editingMarket.value.countries,
    }
    await saveMarketForPack(props.packId, market)
    log('CreditPackMarketsPanel', 'handleSaveMarket', 'Mercado salvo', {
      packId: props.packId,
      currency: market.currency,
      provider_code: market.provider_code,
    })
    eventBus.emit('globalMessage.success', t('Mercado salvo com sucesso'))
    editingMarket.value = null
    isAddMode.value = false
    await fetchPacks()
    eventBus.emit('creditPack.refresh')
    emit('changed')
  } catch (err) {
    log('CreditPackMarketsPanel', 'handleSaveMarket', 'Erro ao salvar mercado', err)
    eventBus.emit('globalMessage.error', t('Erro ao salvar mercado'))
  } finally {
    isSaving.value = false
  }
}

defineExpose({ editingMarket, isAddMode, currentMarkets, countriesError })
</script>

<style scoped>
.credit-pack-markets-panel {
  margin-top: 16px;
}

.pack-prices-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pack-prices-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid var(--surface-border, #e0e0e0);
}

.market-countries {
  min-width: 90px;
  font-size: 0.85rem;
  opacity: 0.85;
}

.market-currency {
  font-weight: 600;
  min-width: 48px;
}

.market-provider {
  color: var(--text-color-secondary, #6c757d);
  font-size: 0.85rem;
  min-width: 64px;
}

.market-price {
  flex: 1;
}

.pack-prices-add {
  padding-top: 8px;
}

.pack-prices-edit-form .form-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.field-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.field-value-static {
  font-weight: 600;
  padding: 6px 0;
}

.field-full {
  flex: 1 1 100%;
}

.edit-form-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.countries-error {
  color: var(--red-500, #ef4444);
  font-size: 0.8rem;
}
</style>
